import type { Error as DomainError } from '@/lib/api/generated/model';
import { personalAccountRefusal, type MissingHost } from '@/features/identity';

export type MoveRefusal =
  | { kind: 'stale'; message: string }
  | { kind: 'busy'; message: string }
  | { kind: 'illegal'; message: string }
  | { kind: 'missing-profile'; message: string }
  /**
   * The workspace requires the person's own forge account and they have none
   * usable for a host (spec 160). Comes as the violations envelope, which has
   * no `error` code, so it is read before the switch below.
   */
  | { kind: 'needs-account'; message: string; hosts: MissingHost[] }
  | { kind: 'unknown'; message: string };

/**
 * Creation is refused up front only for a workflow this form cannot satisfy.
 * Field-level problems are surfaced by the form's own resolver, and a failed
 * request is reported by the dialog — neither becomes a refusal.
 *
 * `missing-profile` is the one refusal reported *after* a request (spec 122):
 * the start step itself has no linked AI profile, discovered only when the
 * backend rejects the create. It shares `MoveRefusal`'s variant shape so
 * `classifyMoveFailure` can be reused as-is rather than duplicated.
 */
export type CreateRefusal =
  | { kind: 'required-inputs'; inputNames: string[]; message: string }
  | { kind: 'missing-profile'; message: string };

/**
 * What a move that failed outright is reported as.
 *
 * Two wordings from one source, because the two surfaces that offer a move
 * differ in exactly one respect: dragging a card can also say what became of
 * the card, and the drawer's button has no card to speak of. Splitting the
 * shared sentence out keeps the surfaces from drifting into describing the
 * same failure differently, which is the whole of the requirement that they
 * report a failed move the same way.
 */
export const MOVE_FAILED = 'The move could not be completed.';

/** The board's wording: the same failure, plus the fate of the dragged card. */
export const MOVE_FAILED_CARD_RETURNED = `${MOVE_FAILED} The card has been returned.`;

/**
 * The backend's global error handler emits a stable domain-error envelope,
 * `{ error: "<CODE>", message?: string }` (contracts/error-codes.md), for
 * every refusal on the move endpoint. Branching on `error` — not on HTTP
 * status alone — is required because `VERSION_CONFLICT` and
 * `ACTIVE_EXECUTION_EXISTS` share status 409.
 *
 * `TASK_NOT_FOUND` and `TRANSITION_NOT_FOUND` fall through to `unknown`:
 * both indicate a stale board or a client bug rather than a refusal the user
 * caused, and the caller still rolls the card back and refetches for either.
 *
 * `TRANSITION_NOT_ALLOWED` is kept only as a defensive branch. Placement is
 * free (intent 002 BR-002-28): the service checks that the target is a real
 * step of the task's workflow and nothing about which transitions declare it,
 * so no legality refusal is emitted today. Handling it costs one case and
 * keeps a re-tightened server from surfacing as "unknown".
 */
export function classifyMoveFailure(
  _status: number,
  body: DomainError | undefined,
): MoveRefusal {
  const needsAccount = personalAccountRefusal(body);
  if (needsAccount) return { kind: 'needs-account', ...needsAccount };
  switch (body?.error) {
    case 'VERSION_CONFLICT':
      return {
        kind: 'stale',
        message: 'The board was out of date. It has been refreshed.',
      };
    case 'ACTIVE_EXECUTION_EXISTS':
      return { kind: 'busy', message: 'This task is busy right now.' };
    case 'TRANSITION_NOT_ALLOWED':
      return {
        kind: 'illegal',
        message: "That move isn't allowed from here.",
      };
    case 'STEP_MISSING_LLM_PROFILE':
      return {
        kind: 'missing-profile',
        message:
          body?.message ??
          'That step has no AI profile configured. Link one in the workflow editor before moving tasks there.',
      };
    default:
      return {
        kind: 'unknown',
        message: MOVE_FAILED_CARD_RETURNED,
      };
  }
}

/**
 * Client-side busy refusal producer (complementary to classifyMoveFailure).
 * Used by dragHandlers when a task is live or chain-in-progress before
 * the move request is sent. Reuses the exact same message as the server-side
 * `ACTIVE_EXECUTION_EXISTS` path so the user sees consistent wording.
 */
export function clientBusyRefusal(): MoveRefusal {
  return { kind: 'busy', message: 'This task is busy right now.' };
}

export type TaskDeleteErrorKind = 'not-found' | 'busy' | 'timeout' | 'unknown';

/**
 * Why deleting a task failed (spec 137 US2). The dialog switches on `kind`:
 * `not-found` means the task is already gone, which is the outcome the person
 * wanted, so it closes; the others stay open with the reason.
 */
export class TaskDeleteError extends Error {
  readonly kind: TaskDeleteErrorKind;

  constructor(kind: TaskDeleteErrorKind, message: string) {
    super(message);
    this.name = 'TaskDeleteError';
    this.kind = kind;
  }
}

/** The envelope's `message`, when the service sent one worth showing. */
function backendMessage(body: unknown): string | undefined {
  const message = (body as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message.trim() !== ''
    ? message
    : undefined;
}

/** Maps a failed `DELETE …/tasks/{id}` to a typed error. */
export function classifyDeleteFailure(
  status: number,
  body: unknown,
): TaskDeleteError {
  switch (status) {
    case 404:
      return new TaskDeleteError(
        'not-found',
        backendMessage(body) ?? 'This task was already deleted.',
      );
    case 409:
      return new TaskDeleteError(
        'busy',
        backendMessage(body) ??
          'Work restarted on this task while it was being deleted. Try again.',
      );
    case 504:
      return new TaskDeleteError(
        'timeout',
        'Running work could not be confirmed stopped, so nothing was deleted. Try again shortly.',
      );
    default:
      return new TaskDeleteError(
        'unknown',
        backendMessage(body) ?? 'The task could not be deleted.',
      );
  }
}

export type TaskSplitErrorKind =
  | 'not-found'
  | 'not-waiting'
  | 'not-a-child'
  | 'all-children'
  | 'invalid'
  | 'unknown';

/** Why moving subtasks into a new task failed (spec 137 US3). */
export class TaskSplitError extends Error {
  readonly kind: TaskSplitErrorKind;

  constructor(kind: TaskSplitErrorKind, message: string) {
    super(message);
    this.name = 'TaskSplitError';
    this.kind = kind;
  }
}

/** Messages of a 422 validation body, whether `violations[]` or `detail[]`. */
function violationMessages(body: unknown): string | undefined {
  const record = body as
    { violations?: unknown; detail?: unknown } | null | undefined;
  const list = Array.isArray(record?.violations)
    ? record.violations
    : Array.isArray(record?.detail)
      ? record.detail
      : [];
  const messages = list
    .map((item) => {
      const entry = item as { message?: unknown; msg?: unknown };
      return typeof entry.message === 'string'
        ? entry.message
        : typeof entry.msg === 'string'
          ? entry.msg
          : '';
    })
    .filter((text) => text !== '');
  return messages.length > 0 ? messages.join(' ') : undefined;
}

/** Maps a failed `POST …/tasks/{id}/split` to a typed error. */
export function classifySplitFailure(
  status: number,
  body: unknown,
): TaskSplitError {
  const code = (body as { error?: unknown } | null | undefined)?.error;
  const message = backendMessage(body);
  switch (code) {
    case 'TASK_NOT_FOUND':
      return new TaskSplitError(
        'not-found',
        message ?? 'This task no longer exists.',
      );
    case 'SPLIT_PARENT_NOT_WAITING':
      return new TaskSplitError(
        'not-waiting',
        message ?? 'This task is not waiting on its subtasks.',
      );
    case 'SPLIT_NOT_A_CHILD':
      return new TaskSplitError(
        'not-a-child',
        message ?? 'One of the subtasks no longer belongs to this task.',
      );
    case 'SPLIT_ALL_CHILDREN':
      return new TaskSplitError(
        'all-children',
        message ?? 'Leave at least one subtask with the original task.',
      );
  }
  if (status === 422) {
    const text = violationMessages(body) ?? message;
    if (text !== undefined) return new TaskSplitError('invalid', text);
  }
  if (status === 404) {
    return new TaskSplitError(
      'not-found',
      message ?? 'This task no longer exists.',
    );
  }
  return new TaskSplitError(
    'unknown',
    message ?? 'The subtasks could not be moved.',
  );
}
